import { useCallback, useEffect, useState } from 'react';
import { BillingApi } from './billingApi';
import { humanizeCode } from './format';
import { AdminPlan, PlanFeatureKey } from './types';

/**
 * Plan list shared by every admin screen that shows or picks plans.
 * One request per page load; call `refresh` after creating or editing plans.
 */
let cached: Promise<AdminPlan[]> | null = null;

function loadPlans(force = false): Promise<AdminPlan[]> {
  if (!cached || force) {
    cached = BillingApi.plans().then((res) => {
      if (!res.success || !res.data) {
        cached = null;
        throw new Error(res.error || res.message || 'Failed to load plans');
      }
      return res.data;
    });
  }
  return cached;
}

export function invalidatePlanCatalog(): void {
  cached = null;
}

export function usePlanCatalog() {
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force: boolean) => {
    setLoading(true);
    try {
      setPlans(await loadPlans(force));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const byCode = useCallback(
    (code: string | null | undefined) => plans.find((p) => p.code === code),
    [plans]
  );

  const planName = useCallback(
    (code: string | null | undefined) => byCode(code)?.name ?? humanizeCode(code),
    [byCode]
  );

  const planHasFeature = useCallback(
    (code: string | null | undefined, feature: PlanFeatureKey) =>
      !!byCode(code)?.features.includes(feature),
    [byCode]
  );

  return {
    plans,
    loading,
    error,
    refresh: () => load(true),
    byCode,
    planName,
    planHasFeature
  };
}
